<template>
  <div class="groups-page">
    <section class="resource-toolbar">
      <div>
        <h2>Núcleos</h2>
        <p>Organize ruas, condomínios e comércios com cobrança própria.</p>
      </div>
      <div class="toolbar-actions">
        <v-btn outlined color="primary" :loading="generating" @click="generateCharges">
          <v-icon left size="18">mdi-calendar-sync-outline</v-icon>
          Gerar mensalidades
        </v-btn>
        <v-btn depressed color="primary" class="toolbar-create-button" @click="openDialog()">
          <v-icon left size="18">mdi-plus</v-icon>
          Adicionar
        </v-btn>
      </div>
    </section>

    <ListFilters
      v-model="filterDraft"
      :fields="filterFields"
      :loading="loading"
      @filter="applyFilters"
      @clear="applyFilters"
    />

    <section class="table-panel">
      <BoaruaDataTable :headers="headers" :items="filteredItems" :loading="loading" item-key="id" class="boarua-table">
        <template v-slot:item.type="{ item }">{{ typeLabel(item.type) }}</template>
        <template v-slot:item.billing_mode="{ item }">{{ billingModeLabel(item.billing_mode) }}</template>
        <template v-slot:item.base_amount="{ item }">{{ formatMoney(item.base_amount) }}</template>
        <template v-slot:item.payer="{ item }">
          {{ item.payer && item.payer.user ? item.payer.user.name : "-" }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small title="Editar" @click="openDialog(item)">
            <v-icon size="19">mdi-pencil-outline</v-icon>
          </v-btn>
        </template>
        <template v-slot:footer.prepend>
          <v-btn icon small :loading="loading" title="Atualizar" @click="load">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </template>
      </BoaruaDataTable>
    </section>

    <v-dialog v-model="dialog" max-width="820">
      <v-card class="form-dialog">
        <v-card-title>
          <div>
            <strong>{{ form.id ? "Editar núcleo" : "Novo núcleo" }}</strong>
            <span>Defina cobrança, valor e responsável quando houver boleto centralizado.</span>
          </div>
        </v-card-title>

        <v-card-text>
          <v-form ref="formRef">
            <v-row dense>
              <v-col cols="12" md="7">
                <v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto" :rules="[requiredRule]" />
              </v-col>
              <v-col cols="12" md="5">
                <v-select v-model="form.type" :items="typeOptions" label="Tipo" outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="form.billing_mode" :items="billingModes" label="Formato de cobrança" outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.billing_calculation"
                  :items="calculationOptions"
                  label="Cálculo"
                  outlined
                  dense
                  hide-details="auto"
                  :disabled="form.billing_mode !== 'centralized'"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-currency-field
                  v-model="form.base_amount"
                  label="Valor base"
                  outlined
                  dense
                  hide-details="auto"
                  :allow-negative="false"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.billing_quantity_source"
                  :items="quantitySources"
                  label="Quantidade por"
                  outlined
                  dense
                  hide-details="auto"
                  :disabled="form.billing_calculation !== 'per_member'"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.due_day" label="Dia de vencimento" type="number" min="1" max="28" outlined dense hide-details="auto" />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.payer_member_id"
                  :items="members"
                  item-value="id"
                  :item-text="memberLabel"
                  label="Responsável pelo pagamento"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  :disabled="form.billing_mode !== 'centralized'"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.allowed_payment_methods"
                  :items="paymentMethods"
                  label="Meios aceitos neste núcleo"
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <div class="form-section-title">
                  <v-icon size="18">mdi-map-marker-outline</v-icon>
                  <div>
                    <strong>Endereço opcional</strong>
                    <span>Use quando o núcleo representar um condomínio, comércio ou local físico conhecido.</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="addressSelection"
                  :search-input.sync="addressSearch"
                  :items="addressOptions"
                  :loading="searchingAddress"
                  item-text="display_name"
                  label="Endereço"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  return-object
                  no-filter
                  @update:search-input="queueAddressSearch"
                  @change="applyAddressSuggestion"
                />
              </v-col>
              <v-col cols="12">
                <div class="group-map-picker">
                  <div class="group-map-toolbar">
                    <div>
                      <strong>Pin no mapa</strong>
                      <span>Pesquise o endereço ou clique no mapa para ajustar a localização do núcleo.</span>
                    </div>
                    <v-btn outlined color="primary" :loading="searchingAddress" @click="locateGroupAddress">
                      <v-icon left size="18">mdi-map-search-outline</v-icon>
                      Localizar endereço
                    </v-btn>
                  </div>
                  <div ref="groupMap" class="group-map"></div>
                  <div class="group-map-footer">
                    <span v-if="form.latitude && form.longitude">Pin definido no mapa</span>
                    <span v-else>Nenhum pin definido</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.description" label="Observações" outlined dense rows="2" hide-details="auto" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn depressed color="primary" :loading="saving" @click="save">
            <v-icon left size="18">mdi-content-save-outline</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";
import ListFilters from "@/views/boarua/components/ListFilters.vue";
import { applyListFilters, initialListFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaContributionGroups",
  components: { BoaruaDataTable, ListFilters },
  data() {
    return {
      loading: false,
      saving: false,
      generating: false,
      dialog: false,
      filterDraft: initialListFilters(),
      activeFilters: initialListFilters(),
      items: [],
      members: [],
      association: null,
      addressSelection: null,
      addressSearch: "",
      addressOptions: [],
      searchingAddress: false,
      addressSearchTimer: null,
      addressSearchRequestId: 0,
      groupMap: null,
      groupMarker: null,
      headers: [
        { text: "Nome", value: "name" },
        { text: "Tipo", value: "type" },
        { text: "Cobrança", value: "billing_mode" },
        { text: "Valor base", value: "base_amount" },
        { text: "Pagador", value: "payer" },
        { text: "", value: "actions", sortable: false, align: "end", width: 80 },
      ],
      form: this.initialForm(),
      typeOptions: [
        { text: "Rua", value: "street" },
        { text: "Condomínio", value: "condominium" },
        { text: "Comércio", value: "commerce" },
      ],
      billingModes: [
        { text: "Direta por associado", value: "direct" },
        { text: "Centralizada", value: "centralized" },
        { text: "Isento", value: "exempt" },
      ],
      calculationOptions: [
        { text: "Valor fixo", value: "fixed" },
        { text: "Baseado na quantidade", value: "per_member" },
      ],
      quantitySources: [
        { text: "Unidades/endereço", value: "units" },
        { text: "Associados vinculados", value: "members" },
      ],
      paymentMethods: [
        { text: "PIX", value: "pix" },
        { text: "Boleto", value: "boleto" },
        { text: "Cartão", value: "credit_card" },
      ],
    };
  },
  computed: {
    filterFields() {
      return [
        { name: "type", label: "Tipo", type: "select", items: this.typeOptions, primary: true },
        { name: "billing_mode", label: "Cobrança", type: "select", items: this.billingModes, primary: true },
      ];
    },
    filteredItems() {
      return applyListFilters(this.items, this.activeFilters, {
        textKeys: ["name", "type", "billing_mode", "description", "address", "number", "neighborhood", "city", "state", "postal_code"],
        dateKeys: ["created_at", "updated_at"],
      });
    },
  },
  watch: {
    dialog(value) {
      if (value) {
        this.$nextTick(() => {
          this.initGroupMap();
          setTimeout(() => {
            if (this.groupMap) this.groupMap.invalidateSize();
          }, 300);
        });
      } else {
        this.destroyGroupMap();
      }
    },
  },
  mounted() {
    this.load();
    this.loadMembers();
    this.loadAssociation();
  },
  beforeDestroy() {
    this.destroyGroupMap();
    if (this.addressSearchTimer) clearTimeout(this.addressSearchTimer);
  },
  methods: {
    initialForm() {
      return {
        name: "",
        type: "street",
        billing_mode: "direct",
        billing_calculation: "fixed",
        billing_quantity_source: "units",
        base_amount: 0,
        due_day: 10,
        payer_member_id: null,
        allowed_payment_methods: [],
        address: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        postal_code: "",
        latitude: null,
        longitude: null,
        description: "",
        status: "active",
      };
    },
    requiredRule(value) {
      return !!value || "Campo obrigatório";
    },
    applyFilters(filters) {
      this.activeFilters = Object.assign(initialListFilters(), filters || {});
    },
    async load() {
      this.loading = true;
      try {
        const response = await axios.get("/contribution-groups", { params: { limit: 100 } });
        this.items = response.data.content.data || response.data.content || [];
      } finally {
        this.loading = false;
      }
    },
    async loadMembers() {
      const response = await axios.get("/association-members", { params: { limit: 200 } });
      this.members = response.data.content.data || response.data.content || [];
    },
    async loadAssociation() {
      try {
        const response = await axios.get("/associations/current/profile");
        this.association = response.data.content || null;
      } catch (error) {
        this.association = null;
      }
    },
    openDialog(item = null) {
      this.form = item ? Object.assign({}, item, {
        allowed_payment_methods: this.parseMethods(item.allowed_payment_methods),
      }) : this.initialForm();
      this.addressSelection = null;
      this.addressSearch = this.form.address || "";
      this.addressOptions = [];
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
        this.initGroupMap();
      });
    },
    async save() {
      if (this.$refs.formRef && !this.$refs.formRef.validate()) return;
      this.saving = true;
      try {
        this.syncAddressInput();
        const payload = Object.assign({}, this.form);
        if (payload.address) {
          const parsedAddress = this.splitAddressNumber(payload.address);
          if (parsedAddress.number) {
            payload.address = parsedAddress.street;
            if (!payload.number) payload.number = parsedAddress.number;
          }
        }
        payload.postal_code = this.onlyDigits(payload.postal_code);
        payload.state = String(payload.state || "").trim().toUpperCase().slice(0, 2);
        if (payload.billing_mode !== "centralized") payload.payer_member_id = null;
        if (payload.billing_mode !== "centralized") payload.billing_calculation = "fixed";
        if (payload.id) {
          await axios.patch(`/contribution-groups/${payload.id}`, payload);
        } else {
          await axios.post("/contribution-groups", payload);
        }
        this.dialog = false;
        this.destroyGroupMap();
        await this.load();
        this.notifySuccess("Núcleo salvo");
      } catch (error) {
        this.notifyError(error.message || "Não foi possível salvar");
      } finally {
        this.saving = false;
      }
    },
    async generateCharges() {
      this.generating = true;
      try {
        const response = await axios.post("/monthly-charges/generate", {});
        const summary = response.data.content || {};
        this.notifySuccess(`${summary.created || 0} mensalidade(s) gerada(s)`);
      } catch (error) {
        this.notifyError(error.message || "Não foi possível gerar mensalidades");
      } finally {
        this.generating = false;
      }
    },
    queueAddressSearch(value) {
      this.addressSearch = value || "";
      this.syncTypedAddressParts(this.addressSearch);
      if (this.addressSearchTimer) clearTimeout(this.addressSearchTimer);
      if (String(this.addressSearch || "").trim().length < 3) {
        this.addressSearchRequestId += 1;
        this.addressOptions = [];
        this.searchingAddress = false;
        return;
      }
      this.addressSearchTimer = setTimeout(() => this.searchAddress(this.addressSearch), 650);
    },
    async searchAddress(query) {
      if (!query || query.length < 3) {
        this.addressOptions = [];
        return;
      }

      const requestId = ++this.addressSearchRequestId;
      this.searchingAddress = true;
      try {
        const results = await this.fetchAddressCandidates(query);
        if (requestId !== this.addressSearchRequestId) return;
        const ranked = this.uniqueAddressResults(this.rankAddressResults(results, query));
        this.addressOptions = this.enrichAddressOptions(ranked, query).slice(0, 8);
      } catch (error) {
        if (requestId !== this.addressSearchRequestId) return;
        this.addressOptions = [];
      } finally {
        if (requestId === this.addressSearchRequestId) this.searchingAddress = false;
      }
    },
    async fetchAddressCandidates(query, limit = 8) {
      const results = [];
      const seen = new Set();
      const searches = this.addressSearchCandidates(query);
      const context = this.addressSearchContext();

      for (const search of searches) {
        const response = await axios.get("/geocoding/search", {
          params: {
            q: search,
            limit,
            city: context.city || undefined,
            state: context.state || undefined,
            center_lat: Number.isFinite(context.centerLat) ? context.centerLat : undefined,
            center_lng: Number.isFinite(context.centerLng) ? context.centerLng : undefined,
          },
        });
        const items = response.data.content || [];
        (Array.isArray(items) ? items : []).forEach((item) => {
          const key = item.place_id || `${item.lat},${item.lon}`;
          if (!seen.has(key)) {
            seen.add(key);
            results.push(item);
          }
        });
      }

      return results;
    },
    addressSearchCandidates(query) {
      const typed = this.splitAddressNumber(query);
      const restored = this.restoreCommonAddressAccents(query);
      const restoredStreet = this.restoreCommonAddressAccents(typed.street);
      const bases = [query, restored];

      if (typed.number && typed.street) {
        bases.push(`${typed.street}, ${typed.number}`);
        bases.push(`${restoredStreet}, ${typed.number}`);
      }

      return [...new Set(bases.filter(Boolean))].slice(0, 2);
    },
    addressSearchContext() {
      const mapCenter = this.groupMap ? this.groupMap.getCenter() : null;
      return {
        city: String((this.association && this.association.city) || "").trim(),
        state: String((this.association && this.association.state) || "").trim(),
        centerLat: Number((this.association && this.association.center_lat) || (mapCenter && mapCenter.lat)),
        centerLng: Number((this.association && this.association.center_lng) || (mapCenter && mapCenter.lng)),
      };
    },
    restoreCommonAddressAccents(value) {
      return String(value || "")
        .replace(/\babaete\b/gi, (match) => this.matchCase(match, "abaeté"));
    },
    matchCase(original, replacement) {
      if (original === original.toUpperCase()) return replacement.toUpperCase();
      if (original.charAt(0) === original.charAt(0).toUpperCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1);
      }
      return replacement;
    },
    rankAddressResults(results, query) {
      const normalizedQuery = this.normalizeSearchText(query);
      const typed = this.splitAddressNumber(query);
      const normalizedStreet = this.normalizeSearchText(typed.street || query);
      const context = this.addressSearchContext();
      const city = this.normalizeSearchText(context.city);
      const state = this.normalizeSearchText(context.state);

      return results.sort((a, b) => {
        return this.addressResultScore(b, normalizedQuery, normalizedStreet, city, state, context.centerLat, context.centerLng)
          - this.addressResultScore(a, normalizedQuery, normalizedStreet, city, state, context.centerLat, context.centerLng);
      });
    },
    addressResultScore(result, normalizedQuery, normalizedStreet, city, state, centerLat, centerLng) {
      const text = this.normalizeSearchText(result && result.display_name);
      const lat = Number(result && result.lat);
      const lng = Number(result && result.lon);
      let score = 0;

      if (normalizedStreet && text.includes(normalizedStreet)) score += 90;
      if (text.includes(normalizedQuery)) score += 30;
      normalizedStreet.split(" ").filter(Boolean).forEach((part) => {
        if (text.includes(part)) score += 12;
      });
      if (city && text.includes(city)) score += 120;
      if (state && text.includes(state)) score += 40;

      if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(centerLat) && Number.isFinite(centerLng)) {
        const distance = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2));
        score += Math.max(0, 80 - distance * 120);
      }

      return score;
    },
    normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    },
    applyAddressSuggestion(item) {
      if (!item) return;
      const address = item.address || {};
      const street = this.addressRoad(item);
      const typedAddress = this.splitAddressNumber(this.addressSearch);

      this.form.address = street;
      this.form.number = address.house_number || typedAddress.number || this.form.number || "";
      this.form.postal_code = item.postcodeReliable && address.postcode ? address.postcode : this.form.postal_code || "";
      this.form.neighborhood = address.suburb || address.neighbourhood || address.quarter || address.city_district || this.form.neighborhood || "";
      this.form.city = address.city || address.town || address.village || address.municipality || this.form.city || "";
      this.form.state = this.stateToUf(address.state) || this.form.state || "";
      this.addressSearch = street;

      const latitude = Number(item.lat);
      const longitude = Number(item.lon);
      if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
        this.setGroupLocation(latitude, longitude, true);
      }
    },
    async locateGroupAddress() {
      this.syncAddressInput();
      const query = [
        this.form.address,
        this.form.number,
        this.form.neighborhood,
        this.form.city,
        this.form.state,
        this.form.postal_code,
      ].filter(Boolean).join(", ");

      if (!query) {
        this.notifyError("Informe um endereço para localizar");
        return;
      }

      const results = await this.fetchAddressCandidates(query, 6);
      const item = this.rankAddressResults(results, query)[0] || null;
      if (item) {
        this.applyAddressSuggestion(item);
      } else {
        this.notifyError("Endereço não encontrado");
      }
    },
    syncAddressInput() {
      if (this.addressSearch && (!this.form.address || this.form.address !== this.addressSearch)) {
        const parsed = this.splitAddressNumber(this.addressSearch);
        this.form.address = parsed.street || this.addressSearch;
        if (!this.form.number && parsed.number) this.form.number = parsed.number;
      }
    },
    splitAddressNumber(value) {
      const text = String(value || "").trim();
      const match = text.match(/^(.+?)(?:,\s*|\s+n(?:[ºo.]|umero)?\s*|\s+)(\d{1,6}[A-Za-z]?)$/i);
      if (!match) return { street: text, number: "" };
      return {
        street: match[1].trim(),
        number: match[2].trim(),
      };
    },
    addressRoad(item) {
      const address = item && item.address ? item.address : {};
      return address.road || address.pedestrian || address.residential || address.neighbourhood || (item && item.display_name) || "";
    },
    uniqueAddressResults(results) {
      const seen = new Set();
      return results.filter((item) => {
        const address = item.address || {};
        const key = [
          this.addressRoad(item),
          address.suburb || address.neighbourhood || address.quarter || address.city_district,
          address.city || address.town || address.village || address.municipality,
          address.state,
        ].map((part) => this.normalizeSearchText(part)).filter(Boolean).join("|");
        const fallback = this.normalizeSearchText(String(item.display_name || "").replace(/,?\s*\d{5}-?\d{3}(?=,|$)/g, ""));
        const dedupeKey = key || fallback || (item.place_id || `${item.lat},${item.lon}`);
        if (seen.has(dedupeKey)) return false;
        seen.add(dedupeKey);
        return true;
      });
    },
    cleanAddressDisplayName(value) {
      return String(value || "")
        .replace(/,?\s*\d{5}-?\d{3}(?=,|$)/g, "")
        .replace(/\s+,/g, ",")
        .replace(/,\s*,/g, ",")
        .trim();
    },
    enrichAddressOptions(results, query) {
      const typedAddress = this.splitAddressNumber(query);
      return results.map((item) => {
        const address = item.address || {};
        const road = this.addressRoad(item);
        const number = address.house_number || typedAddress.number;
        const baseItem = Object.assign({}, item, {
          display_name: this.cleanAddressDisplayName(item.display_name),
          postcodeReliable: !!address.house_number,
        });
        if (!number || address.house_number || !road) return baseItem;

        const displayName = String(baseItem.display_name || "");
        const normalizedDisplay = this.normalizeAddressSearch(displayName).toLowerCase();
        const normalizedCandidate = this.normalizeAddressSearch(`${road}, ${number}`).toLowerCase();
        if (normalizedDisplay.includes(normalizedCandidate)) return baseItem;

        return Object.assign({}, baseItem, {
          display_name: displayName.replace(road, `${road}, ${number}`),
        });
      });
    },
    syncTypedAddressParts(value) {
      const parsed = this.splitAddressNumber(value);
      if (parsed.number) this.form.number = parsed.number;
      return parsed;
    },
    initGroupMap() {
      if (!this.$refs.groupMap || this.groupMap) return;

      const hasLocation = this.form.latitude && this.form.longitude;
      const center = hasLocation ? [Number(this.form.latitude), Number(this.form.longitude)] : [-30.0346, -51.2177];
      this.groupMap = L.map(this.$refs.groupMap, { zoomControl: true }).setView(center, hasLocation ? 16 : 12);

      L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
        attribution: "&copy; OpenStreetMap &copy; CARTO",
        subdomains: "abcd",
        maxZoom: 20,
      }).addTo(this.groupMap);

      this.groupMap.on("click", (event) => {
        this.setGroupLocation(event.latlng.lat, event.latlng.lng, false);
      });

      if (hasLocation) this.setGroupLocation(this.form.latitude, this.form.longitude, false);
      setTimeout(() => {
        if (this.groupMap) this.groupMap.invalidateSize();
      }, 250);
    },
    destroyGroupMap() {
      if (this.groupMap) {
        this.groupMap.remove();
        this.groupMap = null;
        this.groupMarker = null;
      }
    },
    setGroupLocation(lat, lng, fit = false) {
      const latitude = Number(lat);
      const longitude = Number(lng);
      if (Number.isNaN(latitude) || Number.isNaN(longitude)) return;

      this.form.latitude = latitude;
      this.form.longitude = longitude;

      if (!this.groupMap) return;
      const position = [latitude, longitude];
      if (!this.groupMarker) {
        this.groupMarker = L.marker(position, {
          draggable: true,
          icon: L.divIcon({
            className: "group-location-marker",
            html: '<span><i class="mdi mdi-map-marker"></i></span>',
            iconSize: [34, 34],
            iconAnchor: [17, 31],
          }),
        }).addTo(this.groupMap);
        this.groupMarker.on("dragend", (event) => {
          const markerPosition = event.target.getLatLng();
          this.setGroupLocation(markerPosition.lat, markerPosition.lng, false);
        });
      } else {
        this.groupMarker.setLatLng(position);
      }
      if (fit) this.groupMap.setView(position, 17);
    },
    normalizeAddressSearch(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    stateToUf(state) {
      const states = {
        acre: "AC",
        alagoas: "AL",
        amapa: "AP",
        amazonas: "AM",
        bahia: "BA",
        ceara: "CE",
        "distrito federal": "DF",
        "espirito santo": "ES",
        goias: "GO",
        maranhao: "MA",
        "mato grosso": "MT",
        "mato grosso do sul": "MS",
        "minas gerais": "MG",
        para: "PA",
        paraiba: "PB",
        parana: "PR",
        pernambuco: "PE",
        piaui: "PI",
        "rio de janeiro": "RJ",
        "rio grande do norte": "RN",
        "rio grande do sul": "RS",
        rondonia: "RO",
        roraima: "RR",
        "santa catarina": "SC",
        "sao paulo": "SP",
        sergipe: "SE",
        tocantins: "TO",
      };
      const key = this.normalizeAddressSearch(state).toLowerCase();
      return states[key] || String(state || "").trim().toUpperCase().slice(0, 2);
    },
    parseMethods(value) {
      if (Array.isArray(value)) return value;
      try {
        return value ? JSON.parse(value) : [];
      } catch (error) {
        return [];
      }
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    memberLabel(item) {
      return item && item.user ? `${item.user.name} - ${this.formatPhone(item.phone)}` : "-";
    },
    typeLabel(type) {
      const found = this.typeOptions.find((item) => item.value === type);
      return found ? found.text : type || "-";
    },
    billingModeLabel(mode) {
      const found = this.billingModes.find((item) => item.value === mode);
      return found ? found.text : mode || "-";
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatPhone(phone) {
      const digits = String(phone || "").replace(/\D/g, "");
      if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      return phone || "";
    },
    notifySuccess(message) {
      if (this.$dialog && this.$dialog.notify) {
        this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
      }
    },
    notifyError(message) {
      if (this.$dialog && this.$dialog.notify) {
        this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 20px;
  }

  p {
    margin: 3px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-create-button {
  min-height: 38px;
  padding: 0 18px !important;
  border-radius: 8px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.form-section-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 10px 0 2px;
  color: #087f3d;

  strong,
  span {
    display: block;
  }

  strong {
    color: #087f3d;
    font-size: 14px;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
  }
}

.group-map-picker {
  overflow: hidden;
  margin-top: 4px;
  border: 1px solid #d6e4d9;
  border-radius: 8px;
  background: #f8fbf8;
}

.group-map-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #dfe8e1;

  strong,
  span {
    display: block;
  }

  strong {
    color: #111827;
    font-size: 14px;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
  }
}

.group-map {
  width: 100%;
  height: 220px;
  background: #e9f1ed;
}

::v-deep .group-location-marker {
  span {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background: #087f3d;
    border: 3px solid #ffffff;
    border-radius: 50% 50% 50% 10px;
    box-shadow: 0 10px 22px rgba(8, 127, 61, 0.28);
    transform: rotate(-45deg);
  }

  i {
    font-size: 18px;
    line-height: 1;
    transform: rotate(45deg);
  }
}

.group-map-footer {
  padding: 9px 14px;
  color: #087f3d;
  font-size: 12px;
  font-weight: 700;
}

.table-panel {
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.form-dialog {
  border-radius: 8px;

  .v-card__title {
    padding: 22px 24px 10px;

    strong,
    span {
      display: block;
    }

    strong {
      color: #111827;
      font-size: 20px;
    }

    span {
      margin-top: 4px;
      color: #66736b;
      font-size: 13px;
      font-weight: 400;
    }
  }
}

@media (max-width: 860px) {
  .resource-toolbar,
  .toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .group-map-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
